/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router';
import { WAREHOUSE_RELEASES_PAGE_SIZE } from 'constants/pagination';
import useDebounceCallback from 'hooks/useDebounceCallback';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import SortIcon from 'images/v5/icons/filter-lines.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import LoopSvg from 'images/v5/icons/search-md.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Header from 'layout/atoms/Header/Header';
import Input from 'layout/atoms/Input/Input';
import Loader from 'layout/atoms/Loader/Loader';
import WarehouseReleasesListItem from 'layout/atoms/WarehouseReleasesListItem/WarehouseReleasesListItem';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import FiltersBar from 'layout/molecules/FiltersBar/FiltersBar';
import Grid from 'layout/molecules/Grid/Grid';
import Pagination from 'layout/molecules/Pagination';
import WarehouseReleasesList from 'layout/molecules/WarehouseReleasesList/WarehouseReleasesList';
import { useAppDispatch } from 'store/index';
import { clearBreadcrumbs, setBreadcrumbs } from 'store/layoutSlice';
import { resetWarehouseReleaseDetailsState } from 'store/warehouseReleaseDetailsSlice';
import { addRelease, fetchWarehouseReleases, goToNextWarehouseReleasesPage, goToPreviousWarehouseReleasesPage, goToWarehouseReleasesPage, resetFilters, resetPagination, selectWarehouseReleases, 
// selectWarehouseReleasesErrors,
selectWarehouseReleasesCurrentPage, selectWarehouseReleasesFilters, selectWarehouseReleasesPageInfo, selectWarehouseReleasesPageNumbers, selectWarehouseReleasesRefetchFlags, selectWarehouseReleasesStatus, selectWarehouseReleasesTotalCount, setFilterOrder, setFilterSearch, setFilterStatuses, } from 'store/warehouseReleasesSlice';
import Dashboard from 'templates/Dashboard/Dashboard';
import { getMenuLabel } from 'utils/getMenuItem';
function WarehouseReleasesDashboard() {
    const { t, i18n } = useTranslation(['common', 'warehouseReleases']);
    const history = useHistory();
    const dispatch = useAppDispatch();
    const warehouseReleases = useSelector(selectWarehouseReleases);
    const status = useSelector(selectWarehouseReleasesStatus);
    // const errors = useSelector(selectWarehouseReleasesErrors);
    const refetchFlags = useSelector(selectWarehouseReleasesRefetchFlags);
    const filters = useSelector(selectWarehouseReleasesFilters);
    const pageInfo = useSelector(selectWarehouseReleasesPageInfo);
    const currentPage = useSelector(selectWarehouseReleasesCurrentPage);
    const pageNumbers = useSelector(selectWarehouseReleasesPageNumbers);
    const totalCount = useSelector(selectWarehouseReleasesTotalCount);
    const debouncedSetFilterName = useDebounceCallback((value) => dispatch(setFilterSearch({ value })), 500);
    const statusesOptions = [
        {
            text: i18n.t('warehouseReleases:status.DRAFT'),
            value: 'DRAFT',
        },
        {
            text: i18n.t('warehouseReleases:status.APPROVED'),
            value: 'APPROVED',
        },
        {
            text: i18n.t('warehouseReleases:status.CANCELLED'),
            value: 'CANCELLED',
        },
        {
            text: i18n.t('warehouseReleases:status.DONE'),
            value: 'DONE',
        },
    ];
    const orderOptions = [
        {
            text: i18n.t('common:modified_date'),
            value: '-modifiedDate',
        },
        {
            text: i18n.t('common:created_date'),
            value: '-createdDate',
        },
    ];
    useEffect(() => {
        dispatch(fetchWarehouseReleases());
        dispatch(resetWarehouseReleaseDetailsState());
    }, [dispatch]);
    useEffect(() => {
        const label = getMenuLabel('/warehouse-releases', t);
        if (label) {
            dispatch(setBreadcrumbs([{ to: '/warehouse-releases', name: label }]));
        }
        return () => {
            dispatch(clearBreadcrumbs());
            dispatch(resetFilters());
        };
    }, [dispatch, t]);
    useEffect(() => {
        if (refetchFlags.fetch) {
            dispatch(fetchWarehouseReleases());
        }
    }, [dispatch, refetchFlags.fetch]);
    useEffect(() => {
        dispatch(resetPagination());
        dispatch(fetchWarehouseReleases());
    }, [dispatch, filters.search, filters.order, filters.statuses]);
    const handleNextPage = useCallback(() => {
        dispatch(goToNextWarehouseReleasesPage());
    }, [dispatch]);
    const handlePreviousPage = useCallback(() => {
        dispatch(goToPreviousWarehouseReleasesPage());
    }, [dispatch]);
    const handleGoToPage = useCallback((page) => {
        dispatch(goToWarehouseReleasesPage(page));
    }, [dispatch]);
    const handleAddWarehouseRelease = useCallback(() => {
        const action = dispatch(addRelease());
        action.unwrap().then((response) => {
            history.push(`/warehouse-releases/${response.id}`);
        });
    }, [dispatch, history]);
    return (React.createElement(Dashboard, { stickyFooter: (warehouseReleases === null || warehouseReleases === void 0 ? void 0 : warehouseReleases.length) > 0 ? (React.createElement(Pagination, { currentPage: currentPage, pageNumbers: pageNumbers, pageSize: WAREHOUSE_RELEASES_PAGE_SIZE, hasNextPage: pageInfo.hasNextPage, isLoading: status.fetch === 'loading', itemsCount: warehouseReleases.length, totalCount: totalCount, onPreviousPage: handlePreviousPage, onNextPage: handleNextPage, onGoToPage: handleGoToPage })) : null, headerAside: React.createElement(React.Fragment, null,
            React.createElement(Input, { size: "s", leftIcon: React.createElement(LoopSvg, null), placeholder: t('search'), onChange: (e) => debouncedSetFilterName(e.target.value) }),
            React.createElement(Button, { leftIcon: React.createElement(PlusSvg, null), onClick: handleAddWarehouseRelease }, t('warehouseReleases:add_warehouse_release'))), stickyTop: React.createElement(FiltersBar, { searchInput: filters.search ? (React.createElement(Header, null, t('common:search_results_for', { query: filters.search }))) : (React.createElement("div", null)) },
            React.createElement(Dropdown, { trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(SortIcon, null), rightIcon: React.createElement(ChevronDownSvg, null) }, t('warehouseReleases:status')) }, statusesOptions.map((option) => (React.createElement(Button, { key: option.value, align: "left", theme: filters.statuses.includes(option.value) ? 'fill' : 'ghost', onClick: () => dispatch(setFilterStatuses({ value: option.value })) }, option.text)))),
            React.createElement(Dropdown, { trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(SortIcon, null), rightIcon: React.createElement(ChevronDownSvg, null) }, t('sorting')) }, orderOptions.map((option) => (React.createElement(Button, { key: option.value, align: "left", theme: filters.order === option.value ? 'fill' : 'ghost', onClick: () => dispatch(setFilterOrder({ value: option.value })) }, option.text))))) },
        React.createElement(Grid, { vp: 12, hp: 24 }, status.fetch === 'idle' || status.fetch === 'loading' ? (React.createElement(Loader, null)) : (React.createElement(React.Fragment, null,
            React.createElement(WarehouseReleasesList, null, warehouseReleases === null || warehouseReleases === void 0 ? void 0 : warehouseReleases.map((warehouseRelease) => (React.createElement(WarehouseReleasesListItem, { warehouseRelease: warehouseRelease, key: warehouseRelease.id })))),
            (warehouseReleases === null || warehouseReleases === void 0 ? void 0 : warehouseReleases.length) === 0 && (React.createElement(EmptyState, { header: t('common:no_results_header'), subheader: t('common:no_results_subheader') })))))));
}
export default memo(withRouter(WarehouseReleasesDashboard));
